// Pure model behind the DID detail "Call Flow" tab. Turns the legs of one call
// (rows sharing a trace_id) into a left-to-right flow of nodes, a timeline of
// events, and a few headline numbers. No React, no path aliases — so it can be
// checked with `node --experimental-strip-types callFlowModel.check.ts`.
//
// Leg roles: call_leg A_PARTY is always the customer; B_PARTY is the agent or
// any other leg (transfer target, AI agent, ...).

export type Row = Record<string, any>;

export type FlowTone = 'blue' | 'indigo' | 'violet' | 'amber' | 'green' | 'red' | 'slate';

export type FlowNodeKind =
  | 'customer'
  | 'did'
  | 'ivr'
  | 'queue'
  | 'agent'
  | 'ai'
  | 'system'
  | 'end';

export interface FlowFact {
  label: string;
  value: string;
}

export interface FlowNode {
  key: string;
  kind: FlowNodeKind;
  title: string;
  subtitle: string;
  tone: FlowTone;
  facts: FlowFact[];
  chips: string[];
  /** This leg's events, in order (agent / customer nodes only). */
  events?: TimelineEvent[];
  /** Label on the arrow that leads INTO this node. */
  edgeLabel?: string;
  edgeTone?: FlowTone;
  /** Transfer / conference / barge ... edges: when it was started (ms) and by whom. */
  edgeAt?: number;
  edgeBy?: string;
}

export interface TimelineEvent {
  key: string;
  at: number;
  who: string;
  label: string;
  tone: FlowTone;
}

export interface AmountShare {
  company: string;
  value: number;
}

export interface LegBilling {
  key: string;
  who: string;
  role: 'Customer' | 'Agent' | 'AI Agent';
  legType: string;
  startTime: string;
  endTime: string;
  answered: boolean;
  hangupReason: string;
  failureReason: string;
  callSid: string;
  sipCallId: string;
  disposition: any;
  ringSec: number;
  billingSec: number;
  patchSec: number;
  talkSec: number;
  holdSec: number | null;
  muteSec: number | null;
  declinedBy: string;
  pulses: AmountShare[];
  credits: AmountShare[];
}

export interface CallBilling {
  legs: LegBilling[];
  pulseTotals: AmountShare[];
  creditTotals: AmountShare[];
  declinedBy: string;
  pbxName: string;
  retry: { attempt: string; retryCallId: string } | null;
  isRetryKnown: boolean;
}

export interface CallFlow {
  /** agent: rang an agent or a queue. ivr: stayed in the IVR. none: neither. */
  mode: 'agent' | 'ivr' | 'none';
  ivrName: string;
  /** Digits the caller pressed in the IVR. */
  dtmf: string[];
  endReason: string;
  /** call_failure_reason from the CDR (customer leg first), '' when none. */
  failureReason: string;
  billing: CallBilling;
  nodes: FlowNode[];
  timeline: TimelineEvent[];
  answered: boolean;
  answeredBy: string | null;
  transfers: number;
  conferences: number;
  missedBy: string[];
  waitSec: number | null;
  talkSec: number;
  holdSec: number;
  custRingSec: number;
  agentsTried: number;
  agentsTriedList: { key: string; name: string; number: string; id: string }[];
  legPatches: { key: string; who: string; sec: number }[];
  totalSec: number;
  startAt: number | null;
}

export interface BuildCallFlowInput {
  mainCall: Row;
  relatedCalls?: Row[];
  did?: string;
  isOutgoing?: boolean;
  hangupLabel?: (code: any) => string;
  mask?: (value: any) => string;
  ivrHasAi?: boolean;
}

const num = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const str = (v: any) => (v == null ? '' : String(v).trim());

/** Report times are naive IST strings (parsed as local); life_cycle times are ISO. */
export const parseTime = (v: any): number | null => {
  const s = str(v);
  if (!s) return null;
  const t = new Date(s.includes('T') ? s : s.replace(' ', 'T')).getTime();
  return Number.isFinite(t) ? t : null;
};

const parseJson = (raw: any): any => {
  if (raw == null || raw === '') return null;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const isCustomerLeg = (row: Row) => {
  const leg = str(row?.call_leg).toUpperCase();
  if (leg === 'A_PARTY') return true;
  if (leg === 'B_PARTY') return false;
  // Older rows have no call_leg: a leg with agent details is not the customer.
  return !(str(row?.agent_id) || str(row?.agent_name) || str(row?.agent_number));
};

const JOIN_LABEL: Record<string, string> = {
  TRANSFER: 'Transferred',
  TRANSFER_BLIND: 'Blind transfer',
  TRANSFER_ATTENDED: 'Attended transfer',
  CONFERENCE: 'Added to conference',
  BARGE: 'Barged in',
  WHISPER: 'Whisper',
  MONITOR: 'Monitoring',
  CALL_PATCH: 'Call patched',
};

const isAiLeg = (row: Row) => {
  const t = str(row?.leg_type).toUpperCase();
  // ai_agent_id is also stamped on legs an AI transferred to, so leg_type wins when set.
  if (t) return t === 'AI' || t === 'AI_AGENT';
  return Boolean(row?.ai_agent_id);
};

export const formatSeconds = (sec: number) => {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r ? `${m}m ${r}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

// Durations are shown exactly as ClickHouse stores them.
const ringSec = (row: Row) => num(row?.bridge_ring_duration);
const holdSec = (row: Row) => num(row?.total_hold_duration) || num(row?.hold_duration);
const talkSec = (row: Row) => num(row?.billing_duration);
// Stored CDR call_status wins; older rows without it keep billing > 0.
// (inlined, not utilities/callStatus: this file is node-checked without the @ alias)
const connected = (row: Row) => {
  const status = String(row?.call_status || '').toLowerCase();
  return ['connected', 'failed', 'abandoned'].includes(status) ? status === 'connected' : talkSec(row) > 0;
};
// Talk time of a leg = that leg's patch_duration.
const legTalkSec = (row: Row) => num(row?.patch_duration);

const legTypeLabel = (row: Row) => {
  const t = str(row?.leg_type).toUpperCase();
  if (!t) return '';
  if (t === 'AI' || t === 'AI_AGENT') return 'AI Agent';
  if (t.includes('WEBRTC')) return 'WebRTC';
  if (t.includes('MOBILE')) return 'Mobile';
  return str(row.leg_type);
};

const legSortKey = (row: Row) => parseTime(row?.start_time) ?? Number.MAX_SAFE_INTEGER;

const LIFECYCLE_LABELS: Record<string, { label: string; tone: FlowTone } | null> = {
  'Call Initiated': { label: 'Call started', tone: 'blue' },
  'Remote End Ringing': { label: 'Ringing', tone: 'amber' },
  'Call Ringing': { label: 'Ringing', tone: 'amber' },
  'Remote End Answered': { label: 'Answered', tone: 'green' },
  'Call Connected': { label: 'Answered', tone: 'green' },
  'Call Added to Bridge': { label: 'Connected to conversation', tone: 'green' },
  'Call Hungup': { label: 'Hung up', tone: 'slate' },
  'Custom IVR Entered': { label: 'Entered IVR', tone: 'violet' },
  'Call Entered Queue': { label: 'Entered call group', tone: 'indigo' },
  'Queue Timeout Reached': { label: 'Call group timeout', tone: 'red' },
  'Call Exited Queue': { label: 'Left call group', tone: 'indigo' },
  'Call Patched to Agent': { label: 'Patched to agent', tone: 'green' },
  'Call Hold': { label: 'Put on hold', tone: 'amber' },
  'Call Unhold': { label: 'Resumed from hold', tone: 'green' },
  'Call Mute': { label: 'Muted', tone: 'slate' },
  'Call Unmute': { label: 'Unmuted', tone: 'slate' },
  'API Webhook Triggered': { label: 'Webhook sent', tone: 'slate' },
  // Noise for a one-glance view; still in the IVR Journey / Call Events views.
  'Media Playing': null,
  'DTMF Digit Collected': null,
  'Queue Hold Music Playing': null,
  'API Node Webhook Triggered': null,
};

// PBX data still says "queue" (routing_type QUEUE, event names); the UI calls it a call group.
export const queueToCallGroup = (value: any) =>
  typeof value !== 'string'
    ? value
    : value.replace(/\bqueue(s?)\b/gi, (match: string, plural: string) => {
        if (match === match.toUpperCase()) return `CALL GROUP${plural ? 'S' : ''}`;
        return `${match[0] === 'Q' ? 'Call Group' : 'call group'}${plural}`;
      });

const lifecycleEvents = (row: Row, who: string, legKey: string): TimelineEvent[] => {
  const list = parseJson(row?.life_cycle);
  if (!Array.isArray(list)) return [];
  const out: TimelineEvent[] = [];
  list.forEach((item: any, i: number) => {
    const at = parseTime(item?.time);
    if (at == null) return;
    const name = str(item?.event);
    if (name === 'Dtmf Recieved') {
      const digit = str(item?.data?.Digit) || '?';
      const valid = str(item?.data?.Status).toUpperCase() !== 'INVALID';
      out.push({
        key: `${legKey}-lc-${i}`,
        at,
        who,
        label: valid ? `Pressed ${digit}` : `Pressed ${digit} (invalid)`,
        tone: valid ? 'violet' : 'red',
      });
      return;
    }
    const mapped = name in LIFECYCLE_LABELS ? LIFECYCLE_LABELS[name] : { label: queueToCallGroup(name) || 'Event', tone: 'slate' as FlowTone };
    if (!mapped) return;
    // PBX logs 'Call Initiated' twice a few ms apart; keep one.
    const last = out[out.length - 1];
    if (last && last.label === mapped.label && Math.abs(at - last.at) < 1000) return;
    out.push({ key: `${legKey}-lc-${i}`, at, who, label: mapped.label, tone: mapped.tone });
  });
  return out;
};

const syntheticEvents = (row: Row, who: string, legKey: string, hangupLabel: (c: any) => string): TimelineEvent[] => {
  const out: TimelineEvent[] = [];
  const start = parseTime(row?.start_time);
  const answeredAt = parseTime(row?.bridge_time);
  const end = parseTime(row?.end_time);
  if (start != null) out.push({ key: `${legKey}-start`, at: start, who, label: 'Call started', tone: 'blue' });
  if (connected(row) && answeredAt != null) {
    out.push({ key: `${legKey}-ans`, at: answeredAt, who, label: 'Answered', tone: 'green' });
  }
  if (end != null) {
    const reason = hangupLabel(row?.hangup_cause);
    out.push({
      key: `${legKey}-end`,
      at: end,
      who,
      label: connected(row) ? `Hung up${reason ? ` · ${reason}` : ''}` : `Not answered${reason ? ` · ${reason}` : ''}`,
      tone: connected(row) ? 'slate' : 'red',
    });
  }
  return out;
};

export const parseDialedAgents = (raw: any): Row[] => {
  const list = parseJson(raw);
  return Array.isArray(list) ? list.filter(Boolean) : [];
};

export function buildCallFlow(input: BuildCallFlowInput): CallFlow {
  const {
    mainCall,
    relatedCalls = [],
    did = '',
    isOutgoing = false,
    hangupLabel = (c: any) => str(c),
    mask = (v: any) => str(v),
    ivrHasAi = false,
  } = input;

  const seen = new Set<string>();
  const legs = [mainCall, ...relatedCalls]
    .filter((row) => row && (!mainCall?.trace_id || !row.trace_id || row.trace_id === mainCall.trace_id))
    .filter((row) => {
      const k = str(row.call_sid || row.id) || JSON.stringify(row);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => legSortKey(a) - legSortKey(b));

  const customer = legs.find(isCustomerLeg) || mainCall;
  const others = legs.filter((row) => row !== customer);

  const whoFor = (row: Row) =>
    row === customer
      ? 'Customer'
      : row.transferred_by
        ? str(row.transfer_to_name) || mask(row.callee_number) || 'Agent'
        : str(row.agent_name) || mask(row.agent_number) || mask(row.callee_number) || 'Agent';

  // ---- per-leg events (inside each node, and merged into one timeline) ----
  const legEvents = new Map<Row, TimelineEvent[]>();
  legs.forEach((row, i) => {
    const who = whoFor(row);
    const legKey = `l${i}`;
    const fromLifecycle = lifecycleEvents(row, who, legKey);
    const list = fromLifecycle.length ? fromLifecycle : syntheticEvents(row, who, legKey, hangupLabel);
    legEvents.set(row, list.sort((a, b) => a.at - b.at));
  });

  // ---- nodes -------------------------------------------------------------
  const nodes: FlowNode[] = [];
  let transfers = 0;
  let conferences = 0;
  // Earliest dial per created leg (channelId = that leg's call_sid).
  const dialAtBySid = new Map<string, number>();
  legs.flatMap((r) => parseDialedAgents(r.dialed_agent_list)).forEach((d) => {
    const sid = str(d.channelId);
    const at = parseTime(d.time);
    if (sid && at != null && (dialAtBySid.get(sid) ?? Infinity) > at) dialAtBySid.set(sid, at);
  });
  // When: the dial that created the leg, else the leg's own start. Who: the
  // transferring agent, else meta_data.transferredFrom, else the agent on the call before.
  const joinedAtBy = (row: Row): Pick<FlowNode, 'edgeAt' | 'edgeBy'> => {
    const prev = others.slice(0, others.indexOf(row)).filter(connected).pop();
    const prevName = prev ? whoFor(prev) : '';
    return {
      edgeAt: dialAtBySid.get(str(row.call_sid)) ?? parseTime(row.start_time) ?? legEvents.get(row)?.[0]?.at,
      edgeBy:
        str(row.transferred_by) ||
        str(parseJson(row.meta_data)?.transferredFrom?.agentName) ||
        (prevName === 'Agent' ? '' : prevName) ||
        undefined,
    };
  };
  // Legacy rows have no cdr_call_type: a later leg is read as a transfer.
  const joinEdge = (row: Row): Pick<FlowNode, 'edgeLabel' | 'edgeTone' | 'edgeAt' | 'edgeBy'> => {
    const ct = str(row.cdr_call_type).toUpperCase();
    if (ct && !ct.startsWith('TRANSFER') && JOIN_LABEL[ct]) {
      if (ct === 'CONFERENCE') conferences += 1;
      return { edgeLabel: JOIN_LABEL[ct], edgeTone: 'indigo', ...joinedAtBy(row) };
    }
    transfers += 1;
    return { edgeLabel: JOIN_LABEL[ct] ?? 'Transferred', edgeTone: 'violet', ...joinedAtBy(row) };
  };
  const missedBy: string[] = [];

  const customerNumber = isOutgoing ? customer.callee_number : customer.caller_number;
  const customerNode: FlowNode = {
    key: 'customer',
    kind: 'customer',
    title: mask(customerNumber) || 'Unknown',
    subtitle: 'Customer',
    tone: 'blue',
    facts: str(customer.call_failure_reason) ? [{ label: 'Failure', value: str(customer.call_failure_reason) }] : [],
    chips: [customer.circle_series_name, customer.circle_series_operator_name].map(str).filter(Boolean),
    events: legEvents.get(customer) ?? [],
  };
  const didNode: FlowNode = {
    key: 'did',
    kind: 'did',
    title: mask((did !== '-' && did) || (isOutgoing ? customer.caller_number : customer.callee_number)) || '—',
    subtitle: isOutgoing ? 'Caller ID (DID)' : 'Virtual number (DID)',
    tone: 'indigo',
    facts: [],
    chips: [],
  };

  const agentNode = (row: Row, i: number): FlowNode => {
    const ok = connected(row);
    const ai = isAiLeg(row);
    const facts: FlowFact[] = [];
    // Raw stored seconds only (talk time = patch_duration, bridge_ring_duration).
    facts.push({ label: 'Talk Time', value: `${legTalkSec(row)}s` });
    facts.push({ label: 'Ring', value: `${ringSec(row)}s` });
    if (holdSec(row)) facts.push({ label: 'Hold', value: `${holdSec(row)}s` });
    const reason = hangupLabel(row.hangup_cause);
    if (reason) facts.push({ label: ok ? 'Ended' : 'Reason', value: reason });
    if (str(row.call_failure_reason)) facts.push({ label: 'Failure', value: str(row.call_failure_reason) });
    // Transfer legs (backend sets transferred_by): agent_* is the transferer, callee is the target.
    const transferred = str(row.transferred_by);
    const number = transferred
      ? mask(row.callee_number)
      : mask(row.agent_number) || mask(isOutgoing ? row.caller_number : row.callee_number);
    const name = transferred ? str(row.transfer_to_name) : str(row.agent_name);
    return {
      key: `leg-${i}-${str(row.call_sid || row.id)}`,
      kind: ai ? 'ai' : 'agent',
      title: name || (ai ? 'AI Agent' : number || 'Agent'),
      subtitle: [ok ? 'Answered' : 'Not answered', legTypeLabel(row)].filter(Boolean).join(' · '),
      tone: ok ? 'green' : 'red',
      facts,
      chips: [...(name && number ? [number] : []), ...(transferred ? [`From ${transferred}`] : [])],
      events: legEvents.get(row) ?? [],
    };
  };

  let ivrName = '';
  let digits: string[] = [];
  let routedToQueue = false;
  let agentsTried = 0;
  let agentsTriedList: CallFlow['agentsTriedList'] = [];
  const triedEntry = (key: string, name: string, row?: Row): CallFlow['agentsTriedList'][number] => ({
    key,
    name,
    number: row ? mask(row.agent_number) || mask(isOutgoing ? row.caller_number : row.callee_number) : '',
    id: row ? str(row.agent_id) || (isAiLeg(row) ? str(row.ai_agent_id) : '') : '',
  });

  if (isOutgoing) {
    const [firstAgent, ...restAgents] = others;
    if (firstAgent) {
      const n = agentNode(firstAgent, 0);
      n.subtitle = ['Agent', legTypeLabel(firstAgent)].filter(Boolean).join(' · ');
      n.tone = 'blue';
      nodes.push(n);
    } else {
      nodes.push({
        key: 'source',
        kind: 'system',
        title: str(mainCall.campaign_name) || str(mainCall.agent_name) || 'Outbound call',
        subtitle: str(mainCall.campaign_name) ? 'Campaign' : 'Source',
        tone: 'slate',
        facts: [],
        chips: [],
      });
    }
    nodes.push({ ...didNode, edgeLabel: 'Dials out', edgeTone: 'indigo' });
    const custOk = connected(customer);
    const reason = hangupLabel(customer.hangup_cause);
    nodes.push({
      ...customerNode,
      tone: custOk ? 'green' : 'red',
      subtitle: custOk ? 'Customer · Answered' : 'Customer · Not answered',
      facts: [
        { label: 'Talk Time', value: `${legTalkSec(customer)}s` },
        { label: 'Ring', value: `${ringSec(customer)}s` },
        ...(!custOk && reason ? [{ label: 'Reason', value: reason }] : []),
        ...customerNode.facts,
      ],
      edgeLabel: custOk ? 'Answered' : 'No answer',
      edgeTone: custOk ? 'green' : 'red',
    });
    restAgents.forEach((row, i) => {
      if (!connected(row)) missedBy.push(whoFor(row));
      nodes.push({ ...agentNode(row, i + 1), ...joinEdge(row) });
    });
  } else {
    nodes.push(customerNode);
    nodes.push({ ...didNode, edgeLabel: 'Calls', edgeTone: 'blue' });

    ivrName = str(customer.ivr_name) || others.map((r) => str(r.ivr_name)).find(Boolean) || '';
    digits = str(customer.dtmf).split(',').map(str).filter(Boolean);
    const validDigits = new Set(str(customer.valid_dtmf).split(',').map(str).filter(Boolean));
    if (ivrName || str(customer.routing_type).toUpperCase() === 'IVR' || digits.length || ivrHasAi) {
      nodes.push({
        key: 'ivr',
        kind: 'ivr',
        title: ivrName || 'IVR',
        subtitle: ivrHasAi ? 'IVR · AI node' : 'IVR',
        tone: 'violet',
        facts: digits.length ? [{ label: 'Pressed', value: digits.join(' → ') }] : [],
        chips: digits.filter((d) => validDigits.size && !validDigits.has(d)).map((d) => `Invalid ${d}`),
        edgeLabel: 'Routed',
        edgeTone: 'violet',
      });
    }

    const queueName =
      [customer, ...others].map((r) => str(r.queue_name) || str(r.call_group_name)).find(Boolean) || '';
    // The PBX appends an entry each time an agent's extension dials out (e.g. the
    // transferring agent ringing the target logs HANGUP), so keep one per agent,
    // ANSWERED winning over anything else.
    const DIALED_STATE: Record<string, string> = {
      ANSWERED: 'Answered',
      NOANSWER: 'No answer',
      BUSY: 'Busy',
      CANCEL: 'Cancelled',
      CHANUNAVAIL: 'Unavailable',
      CONGESTION: 'Unavailable',
      HANGUP: 'Hung up',
    };
    const dialedState = new Map<string, string>();
    // Every dial attempt, in order, shown as the box's own event list.
    const dialEvents: TimelineEvent[] = [];
    // channelId of a dial = call_sid of the leg it created. `agent` is the dialing
    // extension and `state` the channel's final state, so the leg is the truth.
    const legBySid = new Map(others.map((r) => [str(r.call_sid), r]));
    const dials = parseDialedAgents(customer.dialed_agent_list).sort(
      (a, b) => (parseTime(a.time) ?? 0) - (parseTime(b.time) ?? 0)
    );
    // When each leg answered; a dial placed after any leg already answered
    // (agent or AI) is a transfer and gets its own box, not routing.
    const answeredAts = others
      .map((r) => (legEvents.get(r) ?? []).find((e) => e.label === 'Answered')?.at)
      .filter((t): t is number => t != null);
    dials.forEach((d, i) => {
      const dialAt = parseTime(d.time);
      if (dialAt != null && answeredAts.some((t) => t < dialAt)) return;
      const leg = legBySid.get(str(d.channelId));
      const who = leg
        ? whoFor(leg)
        : str(d.agent_name || d.agentName || d.name) || mask(d.agent_number || d.number || d.agent) || str(d.interface);
      if (!who) return;
      const state = leg ? (connected(leg) ? 'ANSWERED' : 'NOANSWER') : str(d.state).toUpperCase();
      if (!dialedState.has(who)) {
        const e = triedEntry(`t${i}`, who, leg);
        if (!leg) e.number = mask(d.agent_number || d.number) || '';
        agentsTriedList.push(e);
      }
      if (!dialedState.has(who) || state === 'ANSWERED') dialedState.set(who, state);
      const at = parseTime(d.time);
      if (at != null) {
        dialEvents.push({
          key: `dial-${i}`,
          at,
          who,
          label: `Dialed ${who}${state ? ` · ${DIALED_STATE[state] ?? state}` : ''}`,
          tone: state === 'ANSWERED' ? 'green' : 'amber',
        });
      }
    });
    const dialed = [...dialedState].map(([who, state]) =>
      [who, DIALED_STATE[state] ?? state].filter(Boolean).join(' · ')
    );
    const routing = str(customer.routing_type).toUpperCase();
    const isQueue = !!queueName || routing === 'QUEUE' || routing === 'CALL GROUP';
    agentsTried = dialed.length || others.length;
    if (!dialed.length) agentsTriedList = others.map((r, i) => triedEntry(`t${i}`, whoFor(r), r));
    if (isQueue || dialed.length) {
      routedToQueue = true;
      const facts: FlowFact[] = [];
      if (dialed.length) facts.push({ label: 'Agents dialed', value: String(dialed.length) });
      nodes.push({
        key: 'queue',
        kind: 'queue',
        title: isQueue ? queueName || 'Call Group' : 'Agent routing',
        subtitle: isQueue ? 'Call group' : 'Dialed agents',
        tone: 'indigo',
        facts,
        chips: dialed,
        events: dialEvents.sort((a, b) => a.at - b.at),
        edgeLabel: isQueue ? 'Routed to call group' : 'Dialed',
        edgeTone: 'indigo',
      });
    }

    let prev: Row | null = null;
    others.forEach((row, i) => {
      const n = agentNode(row, i);
      if (!prev) {
        n.edgeLabel = connected(row) ? 'Rang & answered' : 'Rang';
        n.edgeTone = connected(row) ? 'green' : 'amber';
      } else if (connected(prev)) {
        Object.assign(n, joinEdge(row));
      } else {
        n.edgeLabel = 'No answer · next';
        n.edgeTone = 'amber';
      }
      if (!connected(row)) missedBy.push(whoFor(row));
      nodes.push(n);
      prev = row;
    });
  }

  const answeredLeg = isOutgoing
    ? connected(customer)
      ? customer
      : null
    : others.find(connected) || null;
  const anyConnected = legs.some(connected);
  const endReason = hangupLabel(customer.hangup_cause);

  const startAt = legs.map((r) => parseTime(r.start_time)).filter((t): t is number => t != null).sort((a, b) => a - b)[0] ?? null;
  const endAt = legs.map((r) => parseTime(r.end_time)).filter((t): t is number => t != null).sort((a, b) => b - a)[0] ?? null;
  // Customer leg start -> end (the customer is on the whole call); all legs if it has no times.
  const custStartAt = parseTime(customer.start_time);
  const custEndAt = parseTime(customer.end_time);
  // Stored total_duration of the customer leg; older rows fall back to its start -> end.
  const totalSec =
    customer.total_duration != null && customer.total_duration !== ''
      ? num(customer.total_duration)
      : custStartAt != null && custEndAt != null
      ? Math.max(0, (custEndAt - custStartAt) / 1000)
      : startAt != null && endAt != null
        ? (endAt - startAt) / 1000
        : 0;
  const ivrNodeShown = nodes.some((n) => n.kind === 'ivr');

  nodes.push({
    key: 'end',
    kind: 'end',
    title: anyConnected ? 'Call completed' : 'Call not connected',
    subtitle: endReason || 'Ended',
    tone: anyConnected ? 'green' : 'red',
    facts: [],
    chips: [],
    edgeLabel: 'Ends',
    edgeTone: 'slate',
  });

  // ---- timeline ----------------------------------------------------------
  const timeline = legs.flatMap((row) => legEvents.get(row) ?? []).sort((a, b) => a.at - b.at);

  // ---- billing & durations ----------------------------------------------
  const billing = buildBilling(legs, customer, whoFor, hangupLabel);

  // ---- headline numbers --------------------------------------------------
  const answeredAt = answeredLeg ? parseTime(answeredLeg.bridge_time) : null;
  const custStart = parseTime(customer.start_time);
  const waitSec =
    answeredAt != null && custStart != null && answeredAt >= custStart ? (answeredAt - custStart) / 1000 : null;
  const talk = isOutgoing
    ? talkSec(customer)
    : others.filter(connected).reduce((s, r) => s + talkSec(r), 0) || talkSec(customer);
  const hold = legs.reduce((s, r) => Math.max(s, holdSec(r)), 0);

  return {
    mode: isOutgoing || others.length || routedToQueue ? 'agent' : ivrNodeShown ? 'ivr' : 'none',
    ivrName,
    dtmf: digits,
    endReason,
    failureReason: [customer, ...others].map((r) => str(r.call_failure_reason)).find(Boolean) || '',
    billing,
    nodes,
    timeline,
    answered: Boolean(answeredLeg),
    answeredBy: answeredLeg ? whoFor(answeredLeg) : null,
    transfers,
    conferences,
    missedBy,
    waitSec,
    talkSec: talk,
    holdSec: hold,
    custRingSec: ringSec(customer),
    agentsTried,
    agentsTriedList,
    legPatches: legs.map((r, i) => ({ key: `p${i}`, who: whoFor(r), sec: num(r.patch_duration) })),
    totalSec,
    startAt,
  };
}

// ---- billing ---------------------------------------------------------------
// pulses / credits arrive from the API already scoped to the viewer's company
// (super admin sees every company) as [{company_id, company_name, value}].

const shares = (raw: any): AmountShare[] =>
  (Array.isArray(raw) ? raw : [])
    .map((e: any) => ({
      company: str(e?.company_name) || (e?.company_id != null ? `Company #${e.company_id}` : 'Unassigned'),
      value: num(e?.value),
    }))
    .filter((e) => e.value);

const sumShares = (lists: AmountShare[][]): AmountShare[] => {
  const totals = new Map<string, number>();
  lists.flat().forEach((e) => totals.set(e.company, (totals.get(e.company) || 0) + e.value));
  return [...totals].map(([company, value]) => ({ company, value: Number(value.toFixed(4)) }));
};

/** Same wording as the report download (report-download-service resolveDeclinedBy). */
export const declinedByLabel = (row: Row) => {
  const v = str(row?.declined_by).toLowerCase();
  if (!v) return '';
  if (v === 'self') return isCustomerLeg(row) ? 'Customer' : isAiLeg(row) ? 'AI Agent' : 'Agent';
  if (v === 'customer') return 'Customer';
  if (v === 'agent') return 'Agent';
  if (v === 'system') return 'System';
  if (v === 'none') return 'None';
  return str(row.declined_by);
};

const callDeclinedBy = (customer: Row, agentLegs: Row[]) => {
  const known = (r: Row) => (declinedByLabel(r) === 'None' ? '' : declinedByLabel(r));
  // Last agent leg to hang up itself: on a transfer the first agent/AI also ends "self".
  const selfAgent = agentLegs
    .filter((r) => str(r.declined_by).toLowerCase() === 'self')
    .sort((a, b) => (parseTime(b.end_time) ?? 0) - (parseTime(a.end_time) ?? 0))[0];
  const who =
    known(customer) ||
    (selfAgent ? known(selfAgent) : '') ||
    agentLegs.map(known).find(Boolean) ||
    ([customer, ...agentLegs].some((r) => declinedByLabel(r) === 'None') ? 'None' : '');
  // Customer leg says the agent side ended it: that is the agent still on the call
  // at the end (last to hang up), not one who left a conference earlier.
  const lastAgent = [...agentLegs].sort((a, b) => (parseTime(b.end_time) ?? 0) - (parseTime(a.end_time) ?? 0))[0];
  const ender = known(customer) === 'Agent' ? lastAgent : selfAgent;
  return who === 'Agent' && ender && str(ender.agent_name) ? `Agent (${str(ender.agent_name)})` : who;
};

const isTruthyFlag = (v: any) => ['true', '1'].includes(str(v).toLowerCase());

function buildBilling(
  legs: Row[],
  customer: Row,
  whoFor: (r: Row) => string,
  hangupLabel: (c: any) => string,
): CallBilling {
  const agentLegs = legs.filter((r) => r !== customer);
  const callDeclined = callDeclinedBy(customer, agentLegs);
  // "Agent" alone says nothing; name the agent: the leg itself when it declined
  // (self), else the call-level agent from callDeclinedBy.
  const legDeclinedBy = (row: Row) => {
    const label = declinedByLabel(row);
    if (label !== 'Agent') return label;
    if (str(row.declined_by).toLowerCase() === 'self' && str(row.agent_name)) return `Agent (${str(row.agent_name)})`;
    return callDeclined.startsWith('Agent (') ? callDeclined : label;
  };
  const rows: LegBilling[] = legs.map((row, i) => {
    const isCust = row === customer;
    return {
      key: `b${i}-${str(row.call_sid || row.id)}`,
      who: whoFor(row),
      role: isCust ? 'Customer' : isAiLeg(row) ? 'AI Agent' : 'Agent',
      legType: legTypeLabel(row) || (isCustomerLeg(row) ? 'Mobile' : str(row.call_leg)),
      startTime: str(row.start_time),
      endTime: str(row.end_time),
      answered: connected(row),
      hangupReason: hangupLabel(row.hangup_cause) || str(row.hangup_cause),
      failureReason: str(row.call_failure_reason),
      callSid: str(row.call_sid),
      sipCallId: str(row.sip_call_id),
      disposition: row.disposition ?? null,
      ringSec: ringSec(row),
      billingSec: talkSec(row),
      patchSec: num(row.patch_duration),
      talkSec: legTalkSec(row),
      holdSec: isCust ? null : holdSec(row),
      muteSec: isCust ? null : num(row.mute_duration),
      declinedBy: legDeclinedBy(row),
      pulses: shares(row.pulses_breakdown),
      credits: shares(row.credits_breakdown),
    };
  });
  const retrySource = customer.is_retry != null ? customer : agentLegs.find((r) => r.is_retry != null);
  const isRetry = retrySource ? isTruthyFlag(retrySource.is_retry) : false;
  return {
    legs: rows,
    pulseTotals: sumShares(rows.map((r) => r.pulses)),
    creditTotals: sumShares(rows.map((r) => r.credits)),
    declinedBy: callDeclined,
    pbxName: legs.map((r) => str(r.pbx_name)).find(Boolean) || '',
    retry: isRetry && retrySource
      ? { attempt: str(retrySource.retry_attempt), retryCallId: str(retrySource.call_sid) }
      : null,
    isRetryKnown: Boolean(retrySource),
  };
}

// ---- IVR journey event data --------------------------------------------------
// Some IVR nodes (AI agent nodes in particular) put a whole JSON payload in the
// event. End users only need its outcome, so pull out the few fields that say
// what happened and never show the raw object.

const SUMMARY_KEY_RE =
  /(reason|cause|outcome|result|summary|brief|intent|goal|disposition|decision|resolution|status|sentiment|next_?step|transfer|action|message|language)/i;
const SKIP_KEY_RE = /(url|audio|token|key|secret|password|id$|_id|uuid|prompt|transcript|messages|history)/i;

const humanizeKey = (key: string) =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());

export function summarizeEventData(data: any, limit = 5): FlowFact[] {
  const out: FlowFact[] = [];
  const seen = new Set<string>();
  const visit = (obj: any, depth: number) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;
    for (const [key, value] of Object.entries(obj)) {
      if (out.length >= limit) return;
      if (value == null || value === '' || SKIP_KEY_RE.test(key)) continue;
      if (typeof value === 'object') {
        if (depth < 2) visit(value, depth + 1);
        continue;
      }
      if (!SUMMARY_KEY_RE.test(key)) continue;
      const label = humanizeKey(key);
      if (seen.has(label)) continue;
      seen.add(label);
      const text = String(value);
      out.push({ label, value: text.length > 240 ? `${text.slice(0, 240)}…` : text });
    }
  };
  visit(typeof data === 'string' ? parseJson(data) : data, 0);
  return out;
}

export const isAiIvrEventType = (type: string) => /(^|_)(AI|BOT|LLM|VOICEBOT|GPT)(_|$)/i.test(type);
